import React from "react";
import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  Select,
  SimpleGrid,
  useColorMode,
} from "@chakra-ui/react";
import { BsFilter } from "react-icons/bs";
import TradeFilterChips from "../summary/TradeFilterChips";

/**
 * Home's filter bar mirrors Summary's TradeFilters but with a Time Frame
 * (date_range) select instead of a Tag select, and the From/To date inputs
 * accept disabled flags so picking a time frame disables manual dates.
 *
 * State remains owned by Home so the existing handlers (handleSubmitFilter,
 * handleClearFilter, handleNextPage, etc.) keep their direct read access.
 */
const HomeFilters = ({
  // chips
  filters,
  onRemoveFilter,
  labelMap,
  keysToSkip,

  // collapse
  toggleFilter,
  onToggleFilter,

  // filter values + setters
  filter_trade_type,
  setFilterTradeType,
  filter_security_type,
  setFilterSecurityType,
  filter_trade_direction,
  setFilterTradeDirection,
  filter_switch_time,
  setFilterSwitchDate,
  filter_from_date,
  setFilterFromDate,
  filter_to_date,
  setFilterToDate,

  // ticker autocomplete
  searchTickerValue,
  selectedTickerValue,
  onTickerInputChange,
  onTickerInputClick,
  isDropdownOpen,
  isLoading,
  searchTickerResultItems,

  // date input enable flags
  isFromToDisabled,
  isDateRangeDisabled,

  // misc
  maxDate,
  onSubmit,
  onClear,
}) => {
  const { colorMode } = useColorMode();

  return (
    <Box position="relative" zIndex={2}>
      <HStack spacing={3} flexWrap="wrap">
        <Button
          size="sm"
          variant={colorMode === "light" ? "outline" : "ghost"}
          colorScheme="gray"
          leftIcon={<Icon as={BsFilter} />}
          onClick={onToggleFilter}
          borderRadius="md"
        >
          Filters
        </Button>
        <TradeFilterChips
          filters={filters}
          onRemoveFilter={onRemoveFilter}
          labelMap={labelMap}
          keysToSkip={keysToSkip}
        />
      </HStack>

      <Collapse
        in={toggleFilter}
        animateOpacity
        style={{ overflow: toggleFilter ? "visible" : "hidden" }}
      >
        <Box
          mt={3}
          p={4}
          bg={colorMode === "light" ? "white" : "gray.700"}
          rounded="lg"
          boxShadow="sm"
        >
          <SimpleGrid columns={{ base: 2, md: 4, lg: 7 }} spacing={3}>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Trade Type</FormLabel>
              <Select
                size="sm"
                placeholder="All"
                value={filter_trade_type}
                onChange={(e) => setFilterTradeType(e.target.value)}
              >
                <option>Swing Trade</option>
                <option>Day Trade</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Security Type</FormLabel>
              <Select
                size="sm"
                placeholder="All"
                value={filter_security_type}
                onChange={(e) => setFilterSecurityType(e.target.value)}
              >
                <option>Options</option>
                <option>Shares</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Direction</FormLabel>
              <Select
                size="sm"
                placeholder="All"
                value={filter_trade_direction}
                onChange={(e) => setFilterTradeDirection(e.target.value)}
              >
                <option>Long</option>
                <option>Short</option>
                <option>Call</option>
                <option>Put</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Ticker</FormLabel>
              <div className="ticker-search">
                <Input
                  size="sm"
                  type="name"
                  placeholder="Enter Ticker"
                  value={selectedTickerValue ? selectedTickerValue : searchTickerValue}
                  onChange={onTickerInputChange}
                  onClick={onTickerInputClick}
                />
                {isDropdownOpen && (
                  <ul
                    className={
                      colorMode === "light" ? "search-dropdown" : "search-dropdown search-dropdown-dark"
                    }
                  >
                    {isLoading ? <div>Loading...</div> : searchTickerResultItems}
                  </ul>
                )}
              </div>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Time Frame</FormLabel>
              <Select
                size="sm"
                placeholder="All"
                value={filter_switch_time}
                onChange={(e) => setFilterSwitchDate(e.target.value)}
                disabled={isDateRangeDisabled}
              >
                <option>Year</option>
                <option>Month</option>
                <option>Week</option>
                <option>Day</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>From Date</FormLabel>
              <Input
                size="sm"
                value={filter_from_date}
                type="date"
                max={maxDate}
                min="1900-01-01"
                onChange={(e) => setFilterFromDate(e.target.value)}
                disabled={isFromToDisabled}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>To Date</FormLabel>
              <Input
                size="sm"
                value={filter_to_date}
                type="date"
                max={maxDate}
                min={filter_from_date !== "" ? filter_from_date : "1900-01-01"}
                onChange={(e) => setFilterToDate(e.target.value)}
                disabled={isFromToDisabled}
              />
            </FormControl>
          </SimpleGrid>
          <HStack mt={3} spacing={2}>
            <Button size="sm" colorScheme="blue" onClick={onSubmit}>
              Apply
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorScheme="gray"
              onClick={onClear}
            >
              Clear
            </Button>
          </HStack>
        </Box>
      </Collapse>
    </Box>
  );
};

export default HomeFilters;
